import { useI18n } from '@affine/i18n';
import { cssVar } from '@toeverything/theme';
import { cssVarV2 } from '@toeverything/theme/v2';
import { type ReactNode, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';

import { DriverAuthProvider, useDriverAuth } from './auth/driver-auth-provider';
import { isNative } from './native/platform';
import * as outboxStyles from './offline/outbox.css';
import { OutboxProvider, useOutbox } from './offline/outbox-provider';
import { PickupExec } from './screens/pickup-exec';
import { Pickups } from './screens/pickups';
import { TripLog } from './screens/trip-log';
import { DriverErpProvider } from './services/driver-erp-provider';

/**
 * Gate every driver screen behind a Bearer access token. With no token we kick
 * off the Keycloak Authorization-Code + PKCE redirect (CLAUDE.md #6). Tasks
 * 11-12 render the real screens inside the guard.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { token, login, resuming } = useDriverAuth();

  // No token → kick off the Keycloak Authorization-Code + PKCE redirect. This
  // navigates away, so there is no interim screen (and no user-facing string to
  // hardcode); Task 11 can layer a branded splash on top if desired. We WAIT
  // while a native biometric resume is in flight (`resuming`) so a cold-launch
  // restores the session via the refresh grant instead of redirecting to login.
  // On web `resuming` is always false → behavior is identical to today.
  useEffect(() => {
    if (!resuming && !token) {
      void login();
    }
  }, [resuming, token, login]);

  if (!token) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: cssVarV2('layer/background/primary'),
          color: cssVarV2('text/primary'),
          fontFamily: cssVar('fontFamily'),
        }}
      />
    );
  }

  return <>{children}</>;
}

/** Completes the `/callback` redirect, then returns to the pickups home. */
function CallbackRoute() {
  const { handleCallback } = useDriverAuth();
  const navigate = useNavigate();

  useEffect(() => {
    void handleCallback().then(() => {
      navigate('/', { replace: true });
    });
  }, [handleCallback, navigate]);

  return null;
}

/**
 * F007: an always-reachable "Sign out" affordance. A stale/rejected access
 * token has no in-app recovery path unless a driver can ALSO force a fresh
 * PKCE login on demand (not only on a 401 the ErpService happens to hit) —
 * before this there was no `logout()` call site anywhere in the UI. Renders
 * only while authed (a pre-login splash needs no sign-out button); hidden via
 * `null`, matching {@link PendingSyncBadge}'s pattern below. Rendered as a
 * plain button — positioning lives on the shared {@link TopBar} container
 * (F007 blocker 1: this used to be its OWN fixed element on the opposite,
 * inline-start side, directly over every screen's Back button).
 */
export function SignOutBar() {
  const { token, logout } = useDriverAuth();
  const t = useI18n();
  if (!token) {
    return null;
  }
  return (
    <button
      type="button"
      className={outboxStyles.signOutButton}
      data-testid="driver-signout"
      onClick={logout}
    >
      {t['com.affine.pipe.driver.nav.signout']()}
    </button>
  );
}

/**
 * The "N pickups pending sync" badge. Renders STRICTLY from the outbox
 * `pendingCount` (the live count of on-device ops awaiting replay) and is hidden
 * when nothing is queued — the load-bearing visibility for D4: a driver always
 * knows custody data is still on the device and not yet flushed. Positioning
 * lives on the shared {@link TopBar} container (see {@link SignOutBar}).
 */
function PendingSyncBadge() {
  const { pendingCount } = useOutbox();
  const t = useI18n();
  if (pendingCount === 0) {
    return null;
  }
  return (
    <div className={outboxStyles.pendingBadge} role="status">
      {t['com.affine.pipe.driver.outbox.pending']({ count: pendingCount })}
    </div>
  );
}

/**
 * F007 (blocker 1): the ONE fixed top-of-shell row — "Sign out" clustered
 * with the pending-sync badge on the inline-END side. A single row (rather
 * than two independently-positioned fixed elements) can never straddle both
 * sides of a screen header, and `screenBody`'s reserved
 * `paddingBlockStart` (driver-ui.css.ts, sized from the SAME
 * `topBarClearance` this row's height derives) keeps every screen's own
 * header — including the Back button — clear of it.
 */
export function TopBar() {
  return (
    <div className={outboxStyles.topBar}>
      <SignOutBar />
      <PendingSyncBadge />
    </div>
  );
}

/**
 * The driver route table. The `/callback` route is intentionally OUTSIDE the
 * {@link AuthGuard} (it runs the token exchange). The authed "My Pickups" screen
 * (Task 11) and the `/pickups/:id` pickup-execution flow (Task 12) nest inside
 * the guard.
 */
function DriverRoutes() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackRoute />} />
      <Route
        path="/"
        element={
          <AuthGuard>
            <Pickups />
          </AuthGuard>
        }
      />
      <Route
        path="/pickups/:id"
        element={
          <AuthGuard>
            <PickupExec />
          </AuthGuard>
        }
      />
      {/* P3.4: the Monday Driver Log §6 trip form for one assigned pickup. */}
      <Route
        path="/pickups/:id/trip"
        element={
          <AuthGuard>
            <TripLog />
          </AuthGuard>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Native status-bar theming: match the bar's icon contrast to the current colour
 * scheme (the driver SPA follows `prefers-color-scheme` via the AFFiNE tokens), and
 * re-apply on a system light/dark switch. Web is a no-op (`isNative()` false); the
 * @capacitor/status-bar plugin is dynamic-imported only in the native branch so the
 * web bundle never pulls it.
 */
function useNativeStatusBar() {
  useEffect(() => {
    if (!isNative()) {
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    let cancelled = false;
    const apply = () => {
      void (async () => {
        try {
          const { StatusBar, Style } = await import('@capacitor/status-bar');
          if (!cancelled) {
            // Style.Dark = light icons (for a dark bg); Style.Light = dark icons.
            await StatusBar.setStyle({
              style: mq.matches ? Style.Dark : Style.Light,
            });
          }
        } catch {
          /* status-bar plugin unavailable — ignore (non-blocking polish) */
        }
      })();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      cancelled = true;
      mq.removeEventListener('change', apply);
    };
  }, []);
}

export const App = () => {
  useNativeStatusBar();
  return (
    <DriverAuthProvider>
      <DriverErpProvider>
        <OutboxProvider>
          <BrowserRouter>
            <TopBar />
            <DriverRoutes />
          </BrowserRouter>
        </OutboxProvider>
      </DriverErpProvider>
    </DriverAuthProvider>
  );
};
